import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2 } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewSoldVehicles() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search = "", sortCol = "", sortDir = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };
            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("vehicle_sales/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_sold_vehicle`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_sold_vehicle`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("vehicle_sales/delete", { id: id });
                    toast.success("Record deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete record", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = [
        {
            name: "Sl. No",
            selector: (row: any, index: number | undefined) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: "Branch",
            selector: (row: any) => row.branch?.branch_name || "N/A",
            sortable: true,
            sortField: "branch_name",
        },
        {
            name: "Vehicle",
            selector: (row: any) => row.vehicle?.registration_number || "N/A",
            sortable: true,
            sortField: "vehicle_id",
            cell: (row: any) => (
                <div>
                    <span className="fw-bold">{row.vehicle?.registration_number}</span>
                    <small className="d-block text-muted">{row.vehicle?.brand} {row.vehicle?.model}</small>
                </div>
            )
        },
        {
            name: "Buyer Name",
            selector: (row: any) => row.buyer_name,
            sortable: true,
            sortField: "buyer_name",
        },
        {
            name: "Sale Date",
            selector: (row: any) => row.sale_date,
            cell: (row: any) => row.sale_date ? new Date(row.sale_date).toLocaleDateString('en-GB') : '',
            sortable: true,
            sortField: "sale_date",
        },
        {
            name: "Sale Price",
            selector: (row: any) => row.sale_price,
            sortable: true,
            sortField: "sale_price",
            cell: (row: any) => <span className="fw-bold text-success">{Number(row.sale_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        },
        {
            name: "Contact",
            selector: (row: any) => row.buyer_contact,
            sortable: true,
            sortField: "buyer_contact",
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('vehicle-sales.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Vehicle Sales" parent="Operations" title="Sold Vehicles" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Sold Vehicles</H3>
                                {hasPermission('vehicle-sales.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Record Sale
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('vehicle-sales.view') && (
                                <>
                                    <Col xs={12} className="mb-3 d-flex justify-content-end">
                                        <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                    </Col>
                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onSort={handleSort}
                                            sortServer
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer />
        </div>
    );
}
